import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import JsonCodeEditor from "./JsonCodeEditor";
import WidgetParametersEditor from "./WidgetParametersEditor";
import WidgetDraftOutputEditor from "./WidgetDraftOutputEditor";
import { AppTextField } from "./form";
import { AppFormField, AppFormGrid } from "./layout";
import { GRID_HALF } from "../lib/ui/formLayout";
import OpenSearchIndexPicker from "./OpenSearchIndexPicker";
import type { EditableDraftFormState } from "../utils/widgetDraftForm";
import { isValidJson } from "../utils/widgetDraftForm";
import { useAppCustomStyles } from "../assets/theme/custom/hooks";

type Props = {
  value: EditableDraftFormState;
  widgetName?: string;
  onChange: (next: EditableDraftFormState) => void;
  disabled?: boolean;
  autoExpanded?: boolean;
};

export default function WidgetDraftApprovalEditor({
  value,
  widgetName,
  onChange,
  disabled,
  autoExpanded = false,
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.widgetEditor;
  const [dslText, setDslText] = useState(value.dslText);
  const [mappingHintText, setMappingHintText] = useState(value.mappingHintText);

  useEffect(() => {
    setDslText(value.dslText);
    setMappingHintText(value.mappingHintText);
  }, [value.dslText, value.mappingHintText]);

  const dslJsonError = !isValidJson(dslText);
  const mappingHintJsonError = !isValidJson(mappingHintText);

  const patch = (partial: Partial<EditableDraftFormState>) => {
    onChange({ ...value, ...partial });
  };

  const sectionSx = styles.draftApprovalSection;

  return (
    <Box sx={styles.draftApprovalRoot}>
      {autoExpanded && (
        <Alert severity="warning" sx={styles.draftApprovalAlert}>
          {t("widget.draftIncompleteHint")}
        </Alert>
      )}

      <Accordion disableGutters elevation={0} defaultExpanded sx={sectionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={styles.draftApprovalSummary}>
          <Typography variant="caption" sx={styles.draftApprovalTitle}>
            {t("widget.sectionDataSource")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={styles.draftApprovalDetails}>
          <Stack spacing={1}>
            <AppTextField
              label={t("widget.description")}
              value={value.description}
              onChange={(e) => patch({ description: e.target.value })}
              multiline
              minRows={1}
              size="small"
              fullWidth
              disabled={disabled}
            />
            <AppFormGrid spacing="compact">
              <AppFormField size={GRID_HALF}>
                <OpenSearchIndexPicker
                  value={value.indicesText
                    .split(/[,\n]/)
                    .map((s) => s.trim())
                    .filter(Boolean)}
                  onChange={(indices) => patch({ indicesText: indices.join(", ") })}
                  label={t("widget.indices")}
                  size="small"
                  fullWidth
                  disabled={disabled}
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppTextField
                  label={t("widget.timeField")}
                  value={value.timeField}
                  onChange={(e) => patch({ timeField: e.target.value })}
                  size="small"
                  fullWidth
                  disabled={disabled}
                />
              </AppFormField>
            </AppFormGrid>
            <JsonCodeEditor
              label={t("widget.dslTemplate")}
              value={dslText}
              onChange={(text) => {
                if (disabled) return;
                setDslText(text);
                if (!isValidJson(text)) return;
                patch({ dslText: text });
              }}
              height={140}
              error={dslJsonError}
              helperText={t("widget.draftDslHint")}
              readOnly={disabled}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} sx={sectionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={styles.draftApprovalSummary}>
          <Typography variant="caption" sx={styles.draftApprovalTitle}>
            {t("widget.parameters")} ({value.parameters.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={styles.draftApprovalDetailsHidden}>
          <WidgetParametersEditor
            compact
            parameters={value.parameters}
            onChange={(parameters) => patch({ parameters })}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} sx={sectionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={styles.draftApprovalSummary}>
          <Typography variant="caption" sx={styles.draftApprovalTitle}>
            {t("widget.sectionAdvanced")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={styles.draftApprovalDetails}>
          <JsonCodeEditor
            label={t("widget.mappingHint")}
            value={mappingHintText}
            onChange={(text) => {
              if (disabled) return;
              setMappingHintText(text);
              if (!isValidJson(text)) return;
              patch({ mappingHintText: text });
            }}
            height={100}
            error={mappingHintJsonError}
            helperText={t("widget.mappingHintHelp")}
            readOnly={disabled}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} defaultExpanded={autoExpanded} sx={sectionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={styles.draftApprovalSummary}>
          <Typography variant="caption" sx={styles.draftApprovalTitle}>
            {t("widget.outputFormat")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={styles.draftApprovalDetails}>
          <WidgetDraftOutputEditor
            embedded
            format={value.format}
            mapping={value.mapping}
            widgetName={widgetName}
            onChange={({ format, mapping }) => patch({ format, mapping })}
            disabled={disabled}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
